import { useEffect, useCallback, useState, useRef } from 'react';
import SocketService from '../service/socket.service';
import { LLMStreamedResponse, SocketInputEvents, SocketOutputEvents, SocketEvent, Conversation } from '../types/socket';

export const useSocket = () => {
  const [streamingMessage, setStreamingMessage] = useState<LLMStreamedResponse | null>(null);
  const isConnectedRef = useRef(false);
  const inputEventRef = useRef<SocketInputEvents>(SocketEvent.COACHING_CHAT_MESSAGE_STREAM);
  const outputEventRef = useRef<SocketOutputEvents>(SocketEvent.COACHING_CHAT_RESPONSE);

  const connect = useCallback((inputEvent: SocketInputEvents, outputEvent: SocketOutputEvents) => {
    if (!isConnectedRef.current) {
      SocketService.getInstance().connect();
      isConnectedRef.current = true;
      inputEventRef.current = inputEvent;
      outputEventRef.current = outputEvent;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (isConnectedRef.current) {
      SocketService.getInstance().disconnect();
      isConnectedRef.current = false;
    }
  }, []);

  const sendMessage = useCallback((message: Conversation) => {
    const socket = SocketService.getInstance().getSocket();
    if (socket && socket.connected) {
      socket.emit(inputEventRef.current, message);
    } else {
      throw new Error('Socket is not connected');
    }
  }, []);

  useEffect(() => {
    const socket = SocketService.getInstance().getSocket();
    if (socket) {
      const handleResponse = (llmResponse: LLMStreamedResponse) => {
        setStreamingMessage(llmResponse);
      };
      socket.on(outputEventRef.current, handleResponse);

      return () => {
        socket.off(outputEventRef.current, handleResponse);
      };
    }
  }, [isConnectedRef.current]);

  return {
    socket: SocketService.getInstance().getSocket(),
    connect,
    disconnect,
    sendMessage,
    streamingMessage
  };
};
